/**
 * Toasts (SPEC.md §10.4).
 *
 * *"Toasts for every mutation result, with an 'Undo' affordance where a cancel endpoint exists."*
 *
 * The undo is the reason this is a composable rather than a component prop. A document that has
 * been posted cannot be un-posted — but it *can* be cancelled, and the difference between "the
 * system silently did something irreversible" and "the system did something and offered me the way
 * back" is most of what makes an operator trust it. So `undo` is a callback the caller supplies
 * only when a real reversal endpoint exists; there is no fake undo.
 *
 * A module-level store rather than a provide//inject: toasts outlive the component that raised them
 * — a mutation on a form that then navigates away must still show its result.
 *
 * @example
 * const { success, failure } = useToast()
 * success('shipment.posted', { undo: () => cancelShipment(id, reason) })
 */
import { readonly, ref } from 'vue'

export type ToastTone = 'success' | 'error' | 'info'

export interface Toast {
  id: number
  tone: ToastTone
  /** Already-translated text; the caller owns the wording. */
  message: string
  /** Only set when a real reversal exists. */
  undo?: (() => Promise<unknown>) | undefined
  undoLabel?: string
}

/** How long a toast stays. Errors stay longer, because they usually have to be read twice. */
const LIFETIME_MS: Record<ToastTone, number> = {
  success: 4000,
  info: 4000,
  error: 8000,
}

const toasts = ref<Toast[]>([])
let nextId = 1

function dismiss(id: number): void {
  toasts.value = toasts.value.filter((toast) => toast.id !== id)
}

function push(tone: ToastTone, message: string, options: Partial<Toast> = {}): number {
  const id = nextId++
  toasts.value = [...toasts.value, { id, tone, message, ...options }]
  // An undoable toast keeps its full lifetime too: an undo the operator cannot reach in time is
  // not an undo, so the window is deliberately the same generous four seconds.
  setTimeout(() => dismiss(id), LIFETIME_MS[tone])
  return id
}

export function useToast() {
  return {
    toasts: readonly(toasts),
    dismiss,
    success: (message: string, options?: Partial<Toast>) => push('success', message, options),
    // An empty message is a failure answered elsewhere — by a redirect (`describeError`) — and a
    // toast with no words in it would only be a red box.
    failure: (message: string, options?: Partial<Toast>) =>
      message.trim() ? push('error', message, options) : 0,
    info: (message: string, options?: Partial<Toast>) => push('info', message, options),
  }
}
